import React from "react";
import { Sunrise, Sun, Sunset, Moon } from "lucide-react";

/**
 * TimeOfDayBadge — a small, quiet eyebrow rendered at the top of each tab
 * so the whole app breathes with the member's current moment of the day.
 *
 * It doesn't try to guide or explain; it just names where the member is
 * in the day, matching the greeting N8 Guide already uses. That small
 * consistency across tabs makes the experience feel held.
 */
const LABELS = {
  morning:   "Morning",
  afternoon: "Afternoon",
  evening:   "Evening",
  night:     "Night",
};

function bucket(h) {
  if (h >= 5 && h < 12)  return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
}

function IconFor(tod) {
  if (tod === "afternoon") return Sun;
  if (tod === "evening")   return Sunset;
  if (tod === "night")     return Moon;
  return Sunrise;
}

export default function TimeOfDayBadge({ testid = "time-of-day-badge", className = "" }) {
  const [tod, setTod] = React.useState(() => bucket(new Date().getHours()));

  // Re-evaluate on visibility change so a member who leaves the tab open
  // through a boundary (e.g. 4:55 p.m. → 5:05 p.m.) sees the shift on
  // return without a hard reload.
  React.useEffect(() => {
    const check = () => setTod(bucket(new Date().getHours()));
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVis);
    const t = setInterval(check, 10 * 60 * 1000); // every 10 min
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(t);
    };
  }, []);

  const Icon = IconFor(tod);
  return (
    <div
      data-testid={testid}
      data-tod={tod}
      className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--terracotta))] ${className}`}
    >
      <Icon size={11} />
      <span data-testid={`${testid}-label`}>{LABELS[tod]}</span>
    </div>
  );
}
